import { useApi } from '@/hooks/useApi'
import { routes } from '@/utilities/routes'
import { useEffect } from 'react'
import { BooleanFilterType, kbArticleListSpec, type KbArticleRow } from 'aevocam-rest-api-spec'
import { PUBLIC_WEBSITE } from '@/utilities/envVariables'
import { BasicPage } from '@/components/layout/BasicPage'
import { FilterTextBox } from '@/components/scalar/FilterTextBox'
import { Button } from '@/components/action/Button'
import { SelectBox } from '@/components/scalar/SelectBox'
import {
  createDataGridState,
  startLoading,
  doneLoading,
  type DataGridState,
} from '@/components/dataGrid/createDataGridState'
import { DataPager } from '@/components/dataGrid/DataPager'
import { IconButton } from '@/components/action/IconButton'
import { toShortDate } from '@/utilities/formatters'
import { DataGrid, type DataGridColumn } from '@/components/dataGrid/DataGrid'
import type { GlobalStateSetter } from '@/utilities/createGlobalState'

const PAGE_SIZE = 10

type KbArticleListState = DataGridState<KbArticleRow> & {
  filterText?: string
  unpublished: BooleanFilterType
  draft: BooleanFilterType
}

const useKbArticleListState = createDataGridState<KbArticleListState>('kbArticleListState', {
  rows: [],
  totalRows: 0,
  skip: 0,
  take: PAGE_SIZE,
  unpublished: BooleanFilterType.Both,
  draft: BooleanFilterType.Both,
})

export function KbArticleList() {

  const api = useApi({ requireLogin: true, adminOnly: true })
  const [pageState, setPageState] = useKbArticleListState()

  useEffect(() => {
    async function fn() {
      startLoading(setPageState, pageState.refreshQuietly)
      try {
        const refreshIncrement = pageState.refresh ?? 0

        const res = await api.request(kbArticleListSpec, {
          body: {
            skip: pageState.skip,
            take: pageState.take,
            forAdmin: true,
            filterText: pageState.filterText || undefined,
            unpublished: pageState.unpublished,
            draft: pageState.draft ?? BooleanFilterType.Both,
          },
        })
        if (res.status === 200 && res.data?.articles) {
          setPageState(prev => {
            if (prev.refresh !== refreshIncrement)  // This is a state fetch
              return prev
            return {
              ...prev,
              rows: res.data?.articles?.rows ?? [],
              totalRows: res.data?.articles?.totalRows ?? 0,
            }
          })
        }
      } catch (error) {
        console.error(error)
      } finally {
        doneLoading(setPageState)
      }
    }
    void fn()
  }, [
    pageState.skip, pageState.take, pageState.refresh,
    pageState.filterText, pageState.unpublished, pageState.draft,
    setPageState
  ])

  return (
    <BasicPage
      title='Articles'
      toolbarButtons={<>
        <Button
          label='Admin'
          icon='chevronLeft'
          href={routes.admin()}
        />
        <Button
          label='New article'
          icon='plus'
          href={routes.adminKbArticleNew()}
        />
      </>}
    >

      <div className='Cutout'>

        <div className='FlexRow ItemsEnd FlexWrap Gap2'>

          <div className='Wrapped FlexGrow'>

            <FilterTextBox
              label='Filter'
              value={pageState.filterText ?? ''}
              setValue={filterText => setPageState(prev => {
                if (prev.filterText === filterText) return prev
                return ({
                  ...prev,
                  filterText,
                  skip: 0,
                })
              })}
            />

            <SelectBox
              label='Published'
              options={[
                { label: 'Published only', value: BooleanFilterType.No },
                { label: 'Unpublished only', value: BooleanFilterType.Yes },
                { label: 'Either', value: BooleanFilterType.Both },
              ]}
              valueFromString={value => Number(value) as BooleanFilterType}
              value={pageState.unpublished}
              setValue={value => {
                setPageState(prev => ({
                  ...prev,
                  skip: 0,
                  unpublished: Number(value) as BooleanFilterType
                }))
              }}
            />

            <SelectBox
              label='Draft'
              options={[
                { label: 'With changes', value: BooleanFilterType.Yes },
                { label: 'No changes', value: BooleanFilterType.No },
                { label: 'Either', value: BooleanFilterType.Both },
              ]}
              valueFromString={value => Number(value) as BooleanFilterType}
              value={pageState.draft ?? BooleanFilterType.Both}
              setValue={value => {
                setPageState(prev => ({
                  ...prev,
                  skip: 0,
                  draft: Number(value) as BooleanFilterType
                }))
              }}
            />

          </div>

          <div className='Wrapped'>
            <DataPager
              state={pageState}
              setState={setPageState}
              refreshAfterSeconds={10}
              alignRight
            />
          </div>

        </div>

        <DataGrid
          state={pageState}
          setState={setPageState as GlobalStateSetter<DataGridState<KbArticleRow>>}
          idColumn='id'
          columns={columns}
        />

      </div>

    </BasicPage>
  )
}

const columns: DataGridColumn<KbArticleRow>[] = [
  {
    id: 'id',
    className: () => 'NoWrap',
    render: (value, { row, gridProps }) => (<>
      <IconButton
        icon='pencil'
        tooltip='Edit'
        href={routes.adminKbArticleEdit(String(row.id))}
        onClick={() => {
          gridProps.setState(prev => ({
            ...prev,
            selectedId: row.id,
          }))
        }}
      />
      <IconButton
        icon='monitorPlay'
        tooltip='View'
        href={`${PUBLIC_WEBSITE}${row.path}${row.published ? '' : '?preview'}`}
        target='_blank'
        onClick={() => {
          gridProps.setState(prev => ({
            ...prev,
            selectedId: row.id,
          }))
        }}
      />
    </>),
  },
  {
    id: 'path',
    label: 'Path',
    className: () => 'Ellipsis',
    style: () => ({ maxWidth: '20vw' }),
  },
  {
    id: 'title',
    label: 'Title',
    className: () => 'Ellipsis',
    style: () => ({ maxWidth: '20vw' }),
  },
  {
    id: 'published',
    label: 'Published',
    align: 'center',
    render: value => value ? 'X' : '',
  },
  {
    id: 'contentDraft',
    label: 'Draft',
    align: 'center',
    render: (_value, { row }) => row.contentDraft ? 'X' : '',
  },
  {
    id: 'createdAt',
    label: 'Created',
    align: 'right',
    render: (value: string) => toShortDate(value),
  },
]
